import {
  getThemeColor,
} from '../utils/css.ts';

// When changing any color value in this file, update the color swatches in
// services/user_manual/docs/<locale>/general-functionality/color-system.md accordingly.

/**
 * Canonical semantic color palette for Qmulus.
 *
 * Every color that communicates state, urgency, or production status must come
 * from here.
 *
 * Use `SemanticColors` for states that carry an urgency signal (alarms, equipment
 * state, job status indicators). Use `StatisticColors` for purely informational
 * data-visualization contexts (charts, KPIs, OEE dashboards) where the goal is
 * readability, not alerting.
 */
export const SemanticColors = {
  /**
   * Stop everything — safety-critical or catastrophic failure requiring immediate intervention.
   * The operator must act or the situation will worsen.
   * @example EAlarmSeverity.EMERGENCY, life-safety faults
   */
  get emergency() {
    return getThemeColor('--state-emergency');
  },

  /**
   * Act now — something is wrong and unresolved. Distinct from `emergency`:
   * the situation is bad but not yet dangerous.
   * @example EAlarmSeverity.ALARM, unplanned equipment stoppage, unplanned downtime
   */
  get critical() {
    return getThemeColor('--state-critical');
  },

  /**
   * Act soon — not normal, deteriorating, or approaching a limit.
   * Use amber (not yellow): yellow is too close to `idle` gray on light backgrounds.
   * @example EAlarmSeverity.WARNING, soft threshold breach, running-idle state
   */
  get caution() {
    return getThemeColor('--state-caution');
  },

  /**
   * Your turn — the system is paused or incomplete because it needs a deliberate
   * user decision or data entry. Nothing is wrong, but nothing can proceed without the operator.
   * @example Manual mode active, no recipe loaded, task pending review
   */
  get awaitingInput() {
    return getThemeColor('--state-awaiting-input');
  },

  /**
   * Everything is working as intended — normal operation, production running.
   * @example RUNNING asset, finished or in-progress job, completed maintenance task
   */
  get active() {
    return getThemeColor('--state-active');
  },

  /**
   * Expected pause or future event — the system is not running, but that is planned and known.
   * @example Scheduled maintenance, planned downtime, a job not yet started
   */
  get scheduled() {
    return getThemeColor('--state-scheduled');
  },

  /**
   * Connected and reachable, but no production is running.
   * The operator does not need to act — this is a normal resting state.
   * @example Asset IDLE, no production gap, omitted maintenance task
   */
  get idle() {
    return getThemeColor('--state-idle');
  },

  /**
   * No connection or data — the system cannot be reached and its current state is unknown.
   * Darker than `idle` to signal the absence of information, not just the absence of activity.
   * @example Asset OFFLINE, unreachable device
   */
  get unavailable() {
    return getThemeColor('--state-unavailable');
  },

  /**
   * Awareness only — no action required.
   * The operator is being told something, not asked to do something.
   * @example EAlarmSeverity.INFO, assigned signal badge, advisory notice
   */
  get informational() {
    return getThemeColor('--state-informational');
  },
} as const;

/**
 * Muted palette for statistical and data-visualization contexts.
 *
 * These colors share the same hue families as `SemanticColors` but at a weight
 * suited for charts and KPI tiles — they do not carry urgency signals. Use them
 * wherever the goal is readability and comparison, not alerting.
 */
export const StatisticColors = {
  /**
   * Productive time — things are going well.
   * Teal-green, same hue family as `SemanticColors.active`.
   */
  get productive() {
    return getThemeColor('--statistic-productive');
  },

  /**
   * Production planned for the future — expected, but not yet happened.
   * Bright green, same family as `productive` but lighter so the plan stays apart from what was
   * produced. A paler green would be mistaken for `planned`, which sits next to it in the charts.
   */
  get plannedProductive() {
    return getThemeColor('--statistic-planned-productive');
  },

  /**
   * Unplanned loss of production — unexpected and undesired, but no alarm.
   * Dark amber, same hue family as `SemanticColors.caution`.
   */
  get loss() {
    return getThemeColor('--statistic-loss');
  },

  /**
   * Planned or scheduled pause — expected and known in advance.
   * Medium-light cyan, same hue family as `SemanticColors.scheduled`.
   * Lighter than `productive` to maximise visual distinction between the two.
   */
  get planned() {
    return getThemeColor('--statistic-planned');
  },

  /**
   * Operation hold — the asset could have produced but was deliberately left
   * empty for a recorded reason, e.g. an expensive energy tariff window.
   */
  get operationHold() {
    return getThemeColor('--statistic-operation-hold');
  },

  /**
   * Neutral gap — no production, no issue, no schedule.
   * Cool gray, same hue family as `SemanticColors.idle`.
   */
  get gap() {
    return SemanticColors.idle;
  },
} as const;

/**
 * Production-status colors shared across job views, the performance dashboard,
 * and the asset state map. Every entry flows through StatisticColors so that
 * charts and equipment state indicators stay visually in sync.
 */
export const StatusColors = {
  get jobFinished() {
    return StatisticColors.productive;
  },
  get jobPlanned() {
    return StatisticColors.plannedProductive;
  },
  get jobIdle() {
    return StatisticColors.gap;
  },
  get plannedDowntime() {
    return StatisticColors.planned;
  },
  get unplannedDowntime() {
    return StatisticColors.loss;
  },
  get operationHold() {
    return StatisticColors.operationHold;
  },
};
